import axios from 'axios';
import { isTransientAuthError, useAuth } from '@/composables/useAuth';
import router from '@/router';
import { redirectToLogin } from '@/router/navigation';
import { logSafeError } from './logSafeError';
// services/apiService.js
// NOTE: Avoid calling useAuth() at module top-level to prevent lifecycle warnings

const SERVER_URL = process.env.VUE_APP_SERVER_URL;
const axiosInstance = axios.create({
  baseURL: SERVER_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
axiosInstance.interceptors.request.use(
  async (config) => {
    config.baseURL = SERVER_URL;
    const { accessToken, refreshAccessToken, isTokenExpired } = useAuth();
    if (accessToken.value && isTokenExpired()) {
      try {
        await refreshAccessToken();
      } catch (error) {
        if (!isTransientAuthError(error)) redirectToLogin(router);
        return Promise.reject(error);
      }
    }

    if (accessToken.value) {
      config.headers.Authorization = `Bearer ${accessToken.value}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const { accessToken, refreshAccessToken, logout } = useAuth();
    const originalRequest = error.config;

    console.error('Interceptor Error:', logSafeError(error));

    // Ошибки с ответом от сервера
    if (error.response) {
      const { status, data } = error.response;

      if (
        status === 500
        && Number(data?.cause?.status) === 423
        && data?.cause?.code === 'MERCHANT_BLOCKED'
      ) {
        error.response.status = 423;
        return Promise.reject(error);
      }

      // Ignore 401 if we are already logging out or have no token
      if (status === 401 && (!accessToken.value || window.location.pathname.includes('login'))) {
        return Promise.reject(error);
      }

      if (status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          // Обновление токена
          await refreshAccessToken();

          // Добавляем новый токен и повторяем запрос
          originalRequest.headers.Authorization = `Bearer ${accessToken.value}`;
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          console.error('Token refresh failed:', logSafeError(refreshError));
          if (!isTransientAuthError(refreshError)) {
            await logout();
            if (!window.location.pathname.includes('login')) redirectToLogin(router);
          }
          return Promise.reject(refreshError);
        }
      }

      if ([401, 423].includes(status)) {
        return Promise.reject(error);
      }

    }

    // Callers need the status and response body for conflicts and access decisions.
    // A timeout is also a failure, never a successful empty result.
    return Promise.reject(error);
  },
);

export default axiosInstance;

export const getMenuItems = async (data, headers = {}) => {
  const url = '/menu-items';
  headers['target'] = 'test-pos';
  const response = await axiosInstance.post(url, data, { headers });
  return response.items;
};
